import type { ChangeEvent } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiCheckbox from '@mui/material/Checkbox';
import { styled } from '@mui/material';
import { useField } from 'informed';
import {
  WhiteLabelSectionDivider,
  WhiteLabelSectionRoot,
} from './whiteLabelSectionCommon';

export type WhiteLabelInsuranceTypeChoiceRow = {
  value: string;
  label: string;
};

export interface InsuranceTypesSectionProps {
  /** From GraphQL whiteLabelInsuranceTypeChoices (codegen may allow null list items). */
  choices: ReadonlyArray<WhiteLabelInsuranceTypeChoiceRow | null | undefined>;
}

export function InsuranceTypesSection({ choices }: InsuranceTypesSectionProps) {
  const rows = choices.filter(
    (c): c is WhiteLabelInsuranceTypeChoiceRow => c != null,
  );
  const { fieldState, fieldApi, render } = useField({ name: 'types' });
  const types = stringArrayFromFieldValue(fieldState.value);

  const handleTypeChange =
    (value: string) => (event: ChangeEvent<HTMLInputElement>) => {
      fieldApi.setValue(
        nextInsuranceTypes(types, value, event.target.checked),
        event,
      );
    };

  return render(
    <WhiteLabelSectionRoot>
      <Typography variant='h6' gutterBottom>
        Insurance Types
      </Typography>
      <WhiteLabelSectionDivider />

      <TypesList>
        {rows.map((row) => (
          <FormControlLabel
            key={row.value}
            control={
              <MuiCheckbox
                checked={types.includes(row.value)}
                onChange={handleTypeChange(row.value)}
              />
            }
            label={row.label}
          />
        ))}
      </TypesList>
    </WhiteLabelSectionRoot>,
  );
}

/** Normalizes `types` from the Informed field value to a string array. */
function stringArrayFromFieldValue(raw: unknown): Array<string> {
  if (!Array.isArray(raw)) return [];
  const out: Array<string> = [];
  for (const item of raw) {
    if (typeof item === 'string') out.push(item);
  }
  return out;
}

function nextInsuranceTypes(
  current: Array<string>,
  value: string,
  checked: boolean,
): Array<string> {
  if (checked && !current.includes(value)) return [...current, value];
  if (checked) return current;
  return current.filter((type) => type !== value);
}

/** Insurance type checkboxes in a row that wraps on narrow widths */
const TypesList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(1),
}));
